/**
 * Turrets (M6 P20, spec §4 "Turrets"): the auto-aiming weapon family. Each turret mount (`ShipDef.turretMounts`)
 * slews its barrel at the weapon's traverse rate inside its arc, picks the nearest hostile ship or torpedo in arc
 * and reach (the hostility matrix; the weapon's `prefer` class first), leads it and fires through the same gun
 * path as primaries (`launchRound`, D-247's fairness rule). It runs for every ship alike: automatic when the
 * player flies the ship, crewed on AI ships; neither goes through `ControlInput`.
 *
 * Crewed batteries (M10 P5a7b, plan 2026-10-17-m10-p5a Q8): with `weapons.json batteries.playerBatteries` on, the
 * mounts of an L/XL hull (the player's or an AI's alike, D-708) are a crewed battery: the same `pickTurretTarget`
 * (hostiles only, `isLivingEnemy`), but the ship's own target first when it is a living enemy in arc and reach
 * (the player's lock in `Target`, or with no lock the AI pilot's `AIPilot.target`); `barrels` rounds per draw on
 * one mount; and a
 * per-ship hold-fire order (`TurretOrders`; the player toggles it, bindings.json `turretHoldFire`) that keeps the
 * barrels tracking but never firing. With the key off (until W6) every turret runs exactly the path it ran before.
 *
 * Point defence: `stepPointDefence` lets turret rounds (`turret.interceptRadius > 0`) destroy torpedoes of
 * another team, tested along each round's step relative to the torpedo's motion.
 *
 * Per-mount state is module-level typed arrays indexed `eid × MAX_TURRET_MOUNTS + k`, like the ECS components.
 * Every per-tick function is allocation-free (D-014): scratch objects are module-level.
 */
import { inverseRotateVec, rotateVec, type Quat, type Vec3 } from '../core/math'
import type { Rng } from '../core/rng'
import { MAX_TURRET_MOUNTS, type ProjectileWeaponDef, type ShipDef, type WeaponsData } from '../data/schemas'
import { AIPilot, Energy, Hull, Life, MAX_ENTITIES, Pos64, Rot, Ship, Target, Team, Vel } from '../world/components'
import { rotateToward } from './aimAssist'
import { emitEvent, EV, type EventRing } from './events'
import { isHostile } from './hostility'
import { createLeadSolution, LeadArgs, solveLeadWith } from './lead'
import { freeProjectile, type ProjectilePool } from './projectiles'
import { isLivingEnemy, type ShipList } from './targeting'
import { freeTorpedo, type TorpedoPool } from './torpedoes'
import { launchRound } from './weapons'

/** No target. Ship targets are stored as their eid (≥ 0), torpedo targets as `torpedoTarget(slot)` (≤ -2). */
export const TURRET_NO_TARGET = -1
/** Encodes torpedo slot `i` as a turret target. */
export function torpedoTarget(i: number): number {
  return -2 - i
}
/** Cooldown slack (s), as in weapons.ts. */
const COOLDOWN_EPS = 1e-5
/** Safety cap on rounds per mount per tick. */
const MAX_SHOTS_PER_TICK = 8

const SLOTS = MAX_ENTITIES * MAX_TURRET_MOUNTS

/**
 * Per-mount state. `aim*` is the barrel direction in ship space (unit; all zero = not yet aimed, read as the
 * mount facing), `cooldown` seconds until the next round, `target` the current target (see `TURRET_NO_TARGET`).
 */
export const TurretState = {
  aimX: new Float32Array(SLOTS),
  aimY: new Float32Array(SLOTS),
  aimZ: new Float32Array(SLOTS),
  cooldown: new Float32Array(SLOTS),
  target: new Int32Array(SLOTS).fill(TURRET_NO_TARGET),
}

/**
 * Per-ship turret orders (M10 P5a7b): `holdFire` 1 = the ship's crewed battery tracks but never fires. Read only
 * while `weapons.json batteries.playerBatteries` is on, and only for L/XL hulls; set through `setTurretHoldFire`.
 */
export const TurretOrders = { holdFire: new Uint8Array(MAX_ENTITIES) }

/** Sets ship `eid`'s hold-fire order (M10 P5a7b). */
export function setTurretHoldFire(eid: number, on: boolean): void {
  TurretOrders.holdFire[eid] = on ? 1 : 0
}

/** Flips ship `eid`'s hold-fire order and returns the new state (the `turretHoldFire` key, M10 P5a7b). */
export function toggleTurretHoldFire(eid: number): boolean {
  const on = TurretOrders.holdFire[eid] !== 1
  TurretOrders.holdFire[eid] = on ? 1 : 0
  return on
}

/**
 * The player's `turretHoldFire` key (M10 P5a7b, review M2): flips ship `eid`'s hold-fire order only while
 * `weapons.json batteries.playerBatteries` is on; with the key off the press does nothing. Returns whether it acted.
 */
export function pressTurretHoldFire(eid: number, weaponsData: WeaponsData): boolean {
  if (weaponsData.batteries?.playerBatteries !== true) return false
  toggleTurretHoldFire(eid)
  return true
}

/** True when ship def `def` fields a crewed battery under `weaponsData` (M10 P5a7b: the key on, an L/XL hull). */
export function isCrewedBattery(def: ShipDef, weaponsData: WeaponsData): boolean {
  const b = weaponsData.batteries
  return b !== undefined && b.playerBatteries && (def.sizeBand === 'L' || def.sizeBand === 'XL')
}

/** Cumulative counters (perf overlay, tests): rounds fired by turrets and torpedoes they shot down. */
export const TURRET_STATS = { rounds: 0, intercepts: 0 }

/** Index of mount `k` of ship `eid` in `TurretState`. */
export function turretSlot(eid: number, k: number): number {
  return eid * MAX_TURRET_MOUNTS + k
}

/** Clears ship `eid`'s turret state (call at spawn, so a recycled entity id starts with its barrels on their facing). */
export function resetTurrets(eid: number): void {
  for (let k = 0; k < MAX_TURRET_MOUNTS; k++) {
    const s = eid * MAX_TURRET_MOUNTS + k
    TurretState.aimX[s] = 0
    TurretState.aimY[s] = 0
    TurretState.aimZ[s] = 0
    TurretState.cooldown[s] = 0
    TurretState.target[s] = TURRET_NO_TARGET
  }
  TurretOrders.holdFire[eid] = 0
}

/** Clears every mount and the counters (new game). */
export function clearTurrets(): void {
  TurretState.aimX.fill(0)
  TurretState.aimY.fill(0)
  TurretState.aimZ.fill(0)
  TurretState.cooldown.fill(0)
  TurretState.target.fill(TURRET_NO_TARGET)
  TurretOrders.holdFire.fill(0)
  TURRET_STATS.rounds = 0
  TURRET_STATS.intercepts = 0
}

// Scratch (D-014).
const q: Quat = { x: 0, y: 0, z: 0, w: 1 }
const pivot: Vec3 = { x: 0, y: 0, z: 0 }
const faceW: Vec3 = { x: 0, y: 0, z: 0 }
const aim: Vec3 = { x: 0, y: 0, z: 0 }
const want: Vec3 = { x: 0, y: 0, z: 0 }
const fireDir: Vec3 = { x: 0, y: 0, z: 0 }
const clamp: Vec3 = { x: 0, y: 0, z: 0 }
/** World unit direction to the last solved lead point, and the distance to it. */
const leadDir: Vec3 = { x: 0, y: 0, z: 0 }
/**
 * The distance to the last solved lead point (D-014). A class field, not a module `let`: a non-integer written to a
 * module `let` is boxed, one heap number per store.
 */
class LeadDist {
  value = 0
}
const leadDist = new LeadDist()
const lead = createLeadSolution()
/**
 * The turret's own lead arguments (D-014, the `SteerArgs` pattern): the solve is past the callers' inlining budget, and
 * the 13 floats `solveLead` took, then the 6 target floats `solveTurretLead` took, were boxed per call.
 */
const leadArgs = new LeadArgs()
/**
 * The mount's float arguments and `slew`'s result (D-014, the `SteerArgs` pattern): `pickTurretTarget`,
 * `solveTurretLead`, `lineClear` and `slew` are not inlined into `stepTurrets`, so floats passed to them as arguments
 * (the arc, the turn, the clearance) or returned (the remaining angle) were boxed, one heap number per call.
 */
class TurretFloats {
  /** The mount's arc (rad), set per mount by `stepTurrets`. */
  arc = 0
  /** This tick's traverse (rad). */
  turn = 0
  /** The line-of-fire clearance (m). */
  clearance = 0
  /** `slew`'s result: the remaining angle to `want` (rad). */
  off = 0
}
const tf = new TurretFloats()

function angleBetween(ax: number, ay: number, az: number, bx: number, by: number, bz: number): number {
  const c = ax * bx + ay * by + az * bz
  return Math.acos(c > 1 ? 1 : c < -1 ? -1 : c)
}

/**
 * Solves the lead from `pivot` (shooter `eid`'s velocity inherited) to the target in `leadArgs` (`tx`..`tvz`, set by
 * the caller). Writes `leadDir`/`leadDist` and returns true when the round can reach the intercept within
 * its flight time and the lead point is inside the arc around `faceW`.
 */
function solveTurretLead(eid: number, w: ProjectileWeaponDef): boolean {
  const la = leadArgs
  la.sx = pivot.x
  la.sy = pivot.y
  la.sz = pivot.z
  la.svx = Vel.x[eid]
  la.svy = Vel.y[eid]
  la.svz = Vel.z[eid]
  la.speed = w.speed
  if (!solveLeadWith(la, lead)) return false
  if (lead.t * w.speed > w.range) return false
  const dx = lead.x - pivot.x
  const dy = lead.y - pivot.y
  const dz = lead.z - pivot.z
  const d = Math.sqrt(dx * dx + dy * dy + dz * dz)
  if (d <= 0) return false
  leadDir.x = dx / d
  leadDir.y = dy / d
  leadDir.z = dz / d
  leadDist.value = d
  return angleBetween(leadDir.x, leadDir.y, leadDir.z, faceW.x, faceW.y, faceW.z) <= tf.arc
}

function aimAtShip(eid: number, w: ProjectileWeaponDef, o: number): boolean {
  const la = leadArgs
  la.tx = Pos64.x[o]
  la.ty = Pos64.y[o]
  la.tz = Pos64.z[o]
  la.tvx = Vel.x[o]
  la.tvy = Vel.y[o]
  la.tvz = Vel.z[o]
  return solveTurretLead(eid, w)
}

function aimAtTorpedo(eid: number, w: ProjectileWeaponDef, torpedoes: TorpedoPool, i: number): boolean {
  const la = leadArgs
  la.tx = torpedoes.x[i]
  la.ty = torpedoes.y[i]
  la.tz = torpedoes.z[i]
  la.tvx = torpedoes.vx[i]
  la.tvy = torpedoes.vy[i]
  la.tvz = torpedoes.vz[i]
  return solveTurretLead(eid, w)
}

/**
 * The target for a turret of ship `eid` pivoting at `pivot` with its arc centred on `faceW`: the nearest living
 * hostile ship and the nearest hostile live torpedo whose lead points are in arc and reach, the weapon's
 * `prefer` class first, the arc being `tf.arc`. A crewed battery (`battery`, M10 P5a7b) takes the ship's own
 * target (the player's `Target` lock, else the AI pilot's `AIPilot.target`) over the nearest hostile ship when it is a living enemy in arc and reach. Leaves the chosen target's lead
 * in `leadDir`/`leadDist`. Returns the encoded target.
 */
function pickTurretTarget(eid: number, ships: ShipList, torpedoes: TorpedoPool | null, w: ProjectileWeaponDef, battery: boolean): number {
  const reach2 = 4 * w.range * w.range
  let bestShip = -1
  let bestShipD2 = reach2
  let lockedShip = -1
  if (battery) {
    // The ship's target: the player's lock (`Target`, only the player writes it) or, with none, the AI pilot's
    // (`AIPilot.target`; -1 on a ship that has no pilot, reset at spawn and when the pilot is removed). Review M1.
    const lock = Target.eid[eid]
    const o = lock >= 0 ? lock : AIPilot.target[eid]
    if (o >= 0 && o !== eid && isLivingEnemy(eid, o)) {
      const dx = Pos64.x[o] - pivot.x
      const dy = Pos64.y[o] - pivot.y
      const dz = Pos64.z[o] - pivot.z
      if (dx * dx + dy * dy + dz * dz < reach2 && aimAtShip(eid, w, o)) lockedShip = o
    }
  }
  for (let s = 0; s < ships.length; s++) {
    const o = ships[s]
    if (!isLivingEnemy(eid, o)) continue
    const dx = Pos64.x[o] - pivot.x
    const dy = Pos64.y[o] - pivot.y
    const dz = Pos64.z[o] - pivot.z
    const d2 = dx * dx + dy * dy + dz * dz
    if (d2 >= bestShipD2 || !aimAtShip(eid, w, o)) continue
    bestShip = o
    bestShipD2 = d2
  }
  let bestTorp = -1
  let bestTorpD2 = reach2
  if (torpedoes !== null && torpedoes.live > 0) {
    const team = Team.id[eid]
    for (let i = 0; i < torpedoes.capacity; i++) {
      if (torpedoes.alive[i] !== 1 || !isHostile(team, torpedoes.team[i])) continue
      const dx = torpedoes.x[i] - pivot.x
      const dy = torpedoes.y[i] - pivot.y
      const dz = torpedoes.z[i] - pivot.z
      const d2 = dx * dx + dy * dy + dz * dz
      if (d2 >= bestTorpD2 || !aimAtTorpedo(eid, w, torpedoes, i)) continue
      bestTorp = i
      bestTorpD2 = d2
    }
  }
  if (lockedShip >= 0) bestShip = lockedShip
  const torpFirst = w.turret !== undefined && w.turret.prefer === 'torpedo'
  if (bestTorp >= 0 && torpedoes !== null && (torpFirst || bestShip < 0)) {
    aimAtTorpedo(eid, w, torpedoes, bestTorp)
    return torpedoTarget(bestTorp)
  }
  if (bestShip >= 0) {
    aimAtShip(eid, w, bestShip)
    return bestShip
  }
  return TURRET_NO_TARGET
}

/**
 * True when no non-hostile ship (other than `eid`) lies within its collider radius + `tf.clearance` of the line of
 * fire from `pivot` along `fireDir` out to `leadDist` metres (no friendly fire, no stray rounds into neutrals).
 */
function lineClear(eid: number, ships: ShipList, defs: readonly ShipDef[]): boolean {
  const dist = leadDist.value
  const clearance = tf.clearance
  const team = Team.id[eid]
  for (let s = 0; s < ships.length; s++) {
    const o = ships[s]
    if (o === eid || Life.alive[o] !== 1 || isHostile(team, Team.id[o])) continue
    const def = defs[Ship.defIndex[o]]
    const r = (def === undefined ? 0 : def.colliderRadius) + clearance
    const vx = Pos64.x[o] - pivot.x
    const vy = Pos64.y[o] - pivot.y
    const vz = Pos64.z[o] - pivot.z
    const along = vx * fireDir.x + vy * fireDir.y + vz * fireDir.z
    if (along < -r || along > dist + r) continue
    const perp2 = vx * vx + vy * vy + vz * vz - along * along
    if (perp2 < r * r) return false
  }
  return true
}

/**
 * Slews mount `slot`'s ship-space barrel toward the ship-space direction `want` by at most `tf.turn` radians,
 * then keeps it inside the arc `tf.arc` around ship-space `facing`. Leaves the result in `aim` and the state arrays
 * and the remaining angle to `want` in `tf.off`.
 */
function slew(slot: number, facing: Readonly<Vec3>): void {
  const arc = tf.arc
  const turn = tf.turn
  const a = angleBetween(aim.x, aim.y, aim.z, want.x, want.y, want.z)
  rotateToward(aim, want.x, want.y, want.z, a, turn)
  const off = angleBetween(aim.x, aim.y, aim.z, facing.x, facing.y, facing.z)
  if (off > arc) {
    clamp.x = facing.x
    clamp.y = facing.y
    clamp.z = facing.z
    rotateToward(clamp, aim.x, aim.y, aim.z, off, arc)
    aim.x = clamp.x
    aim.y = clamp.y
    aim.z = clamp.z
  }
  TurretState.aimX[slot] = aim.x
  TurretState.aimY[slot] = aim.y
  TurretState.aimZ[slot] = aim.z
  tf.off = angleBetween(aim.x, aim.y, aim.z, want.x, want.y, want.z)
}

/**
 * One tick of every turret mount on every ship in `ships` (sim step 3, with the primaries; before
 * `stepProjectiles`). A living ship's mount picks its target, slews toward the lead point at the traverse rate,
 * and while its barrel is within `fireCone` of it fires rounds at the weapon's rate, paying energy like a gun,
 * unless a non-hostile ship blocks the line of fire (or a crewed battery holds fire). Idle or blocked mounts bank no shots. `rng` is the seeded
 * sim RNG (spread only). Allocation-free.
 */
export function stepTurrets(
  ships: ShipList,
  defs: readonly ShipDef[],
  weaponsData: WeaponsData,
  projectiles: ProjectilePool,
  torpedoes: TorpedoPool | null,
  events: EventRing,
  dt: number,
  rng: Rng,
): void {
  for (let s = 0; s < ships.length; s++) {
    const eid = ships[s]
    const def = defs[Ship.defIndex[eid]]
    if (def === undefined) continue
    const mounts = def.turretMounts
    const n = mounts.length < MAX_TURRET_MOUNTS ? mounts.length : MAX_TURRET_MOUNTS
    if (n === 0) continue
    const battery = isCrewedBattery(def, weaponsData)
    const holdFire = battery && TurretOrders.holdFire[eid] === 1
    const alive = Life.alive[eid] === 1 && Hull.value[eid] > 0
    q.x = Rot.x[eid]
    q.y = Rot.y[eid]
    q.z = Rot.z[eid]
    q.w = Rot.w[eid]
    for (let k = 0; k < n; k++) {
      const slot = eid * MAX_TURRET_MOUNTS + k
      const m = mounts[k]
      const w = weaponsData.weapons[m.weapon]
      let cd = TurretState.cooldown[slot]
      let engaged = false
      if (alive && w !== undefined && w.kind === 'projectile' && w.turret !== undefined) {
        const t = w.turret
        aim.x = TurretState.aimX[slot]
        aim.y = TurretState.aimY[slot]
        aim.z = TurretState.aimZ[slot]
        if (aim.x * aim.x + aim.y * aim.y + aim.z * aim.z < 0.5) {
          aim.x = m.facing.x
          aim.y = m.facing.y
          aim.z = m.facing.z
        }
        rotateVec(q, m.pos.x, m.pos.y, m.pos.z, pivot)
        pivot.x += Pos64.x[eid]
        pivot.y += Pos64.y[eid]
        pivot.z += Pos64.z[eid]
        rotateVec(q, m.facing.x, m.facing.y, m.facing.z, faceW)
        tf.arc = t.arc
        const target = pickTurretTarget(eid, ships, torpedoes, w, battery)
        TurretState.target[slot] = target
        if (target !== TURRET_NO_TARGET) {
          inverseRotateVec(q, leadDir.x, leadDir.y, leadDir.z, want)
          tf.turn = t.traverse * dt
          slew(slot, m.facing)
          if (!holdFire && tf.off <= t.fireCone) {
            rotateVec(q, aim.x, aim.y, aim.z, fireDir)
            tf.clearance = t.clearance
            if (lineClear(eid, ships, defs)) {
              engaged = true
              const interval = 1 / w.rate
              // One draw = one cooldown step; a crewed battery fires `barrels` rounds in it, each paying its energy.
              const barrels = battery ? m.barrels : 1
              let shots = 0
              while (cd <= COOLDOWN_EPS && shots < MAX_SHOTS_PER_TICK) {
                let fired = 0
                while (fired < barrels && Energy.value[eid] >= w.energyCost) {
                  launchRound(eid, m.weapon, w, pivot, fireDir, projectiles, events, rng)
                  TURRET_STATS.rounds++
                  fired++
                }
                if (fired === 0) {
                  engaged = false
                  break
                }
                cd += interval
                shots++
              }
            }
          }
        }
      } else {
        TurretState.target[slot] = TURRET_NO_TARGET
      }
      cd -= dt
      if (cd < 0 && !engaged) cd = 0
      TurretState.cooldown[slot] = cd
    }
  }
}

/**
 * Point defence (M6 P20): after `stepProjectiles` and before `stepTorpedoes`, every live turret round with an
 * `interceptRadius` that passed within that radius of a live torpedo of another team this tick (its step tested
 * relative to the torpedo's own motion, so neither tunnels) destroys the torpedo: `explode` (eid -1, other the
 * round's shooter, the torpedo's weapon, amount = its blast radius) and both are freed. Friendly fire follows
 * the projectile rule: rounds never touch their own team's torpedoes. Allocation-free.
 */
export function stepPointDefence(projectiles: ProjectilePool, torpedoes: TorpedoPool, weaponsData: WeaponsData, events: EventRing, dt: number): void {
  if (torpedoes.live === 0) return
  const high = projectiles.high
  for (let p = 0; p < high; p++) {
    if (projectiles.alive[p] !== 1) continue
    const w = weaponsData.weapons[projectiles.weapon[p]]
    if (w === undefined || w.kind !== 'projectile' || w.turret === undefined || w.turret.interceptRadius <= 0) continue
    const r = w.turret.interceptRadius
    const r2 = r * r
    const team = projectiles.team[p]
    const sx0 = projectiles.px[p]
    const sy0 = projectiles.py[p]
    const sz0 = projectiles.pz[p]
    const mx = projectiles.x[p] - sx0
    const my = projectiles.y[p] - sy0
    const mz = projectiles.z[p] - sz0
    for (let i = 0; i < torpedoes.capacity; i++) {
      if (torpedoes.alive[i] !== 1 || torpedoes.team[i] === team) continue
      const tvx = torpedoes.vx[i] * dt
      const tvy = torpedoes.vy[i] * dt
      const tvz = torpedoes.vz[i] * dt
      // Relative start and step of the round in the torpedo's frame.
      const ox = sx0 - torpedoes.x[i]
      const oy = sy0 - torpedoes.y[i]
      const oz = sz0 - torpedoes.z[i]
      const dx = mx - tvx
      const dy = my - tvy
      const dz = mz - tvz
      const dd = dx * dx + dy * dy + dz * dz
      let u = dd > 0 ? -(ox * dx + oy * dy + oz * dz) / dd : 0
      if (u < 0) u = 0
      else if (u > 1) u = 1
      const cx = ox + dx * u
      const cy = oy + dy * u
      const cz = oz + dz * u
      if (cx * cx + cy * cy + cz * cz > r2) continue
      const tw = weaponsData.weapons[torpedoes.weapon[i]]
      const blast = tw !== undefined && tw.kind === 'torpedo' ? tw.proximity : r
      const ex = torpedoes.x[i] + tvx * u
      const ey = torpedoes.y[i] + tvy * u
      const ez = torpedoes.z[i] + tvz * u
      torpedoes.x[i] = ex
      torpedoes.y[i] = ey
      torpedoes.z[i] = ez
      emitEvent(events, EV.explode, ex, ey, ez, 0, 0, 0, -1, projectiles.owner[p], torpedoes.weapon[i], blast)
      freeTorpedo(torpedoes, i)
      freeProjectile(projectiles, p)
      TURRET_STATS.intercepts++
      break
    }
  }
}
